import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Features } from '@ultrasnip/contracts';
import { type FormEvent, useState } from 'react';
import { z } from 'zod';
import { safeRedirectPath } from '@/app/router';
import { AuthLayout } from '@/components/shell/auth-layout';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { FormAlert, PasswordField, TextField } from '@/features/auth/form-parts';
import { authClient, authErrorMessage } from '@/lib/auth-client';
import { QueryKeys } from '@/lib/queries';

export const Route = createFileRoute('/_auth/login')({
  validateSearch: z.object({ redirect: z.string().max(2048).optional() }),
  component: LoginScreen,
});

function LoginScreen() {
  const { redirect } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError('Enter your email and password.');
      return;
    }
    setPending(true);
    const { data, error: signInError } = await authClient.signIn.email({ email: email.trim(), password });
    setPending(false);
    if (signInError) {
      if (signInError.code === 'EMAIL_NOT_VERIFIED' && Features.emailVerification) {
        await navigate({ to: '/check-email', search: { email: email.trim() } });
        return;
      }
      setError(authErrorMessage(signInError));
      return;
    }
    if (data && 'twoFactorRedirect' in data && data.twoFactorRedirect) {
      await navigate({ to: '/two-factor', search: { redirect } });
      return;
    }
    await queryClient.invalidateQueries({ queryKey: QueryKeys.me });
    await navigate({ to: safeRedirectPath(redirect) });
  };

  return (
    <AuthLayout
      title="Sign in"
      footer={
        <>
          New here?{' '}
          <Link to="/signup" className="link">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <FormAlert message={error} />
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <PasswordField
          label="Password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {Features.email && (
          <div className="-mt-2 flex justify-end">
            <Link
              to="/forgot-password"
              className="rounded text-[0.75rem] text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            >
              Forgot password?
            </Link>
          </div>
        )}
        <Button type="submit" disabled={pending} className="mt-1 h-9 w-full">
          {pending && <Spinner />}
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}
